/**
 * TalentLens AI - Auth Store
 * Zustand store for authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'recruiter' | 'hiring_manager' | 'viewer';
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.login(email, password);
          if (response.success) {
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          }
          set({ isLoading: false, error: 'Login failed' });
          return false;
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Login failed',
          });
          return false;
        }
      },

      logout: async () => {
        try {
          await api.logout();
        } catch (error) {
          // Ignore logout errors
        }
        set({ user: null, isAuthenticated: false });
      },

      checkAuth: async () => {
        // Get current persisted state - if no user stored, skip API call
        const currentState = get();
        if (!currentState.user && !currentState.isAuthenticated) {
          set({ isLoading: false });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await api.me();
          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({ user: null, isAuthenticated: false, isLoading: false });
          }
        } catch (error) {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// Permission helpers
export const hasRole = (user: User | null, ...roles: User['role'][]) => {
  if (!user) return false;
  return roles.includes(user.role);
};

export const isAdmin = (user: User | null) => hasRole(user, 'admin');

// CV permissions
export const canUploadCV = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canDeleteCV = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canArchiveCV = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canViewCV = (user: User | null) => hasRole(user, 'admin', 'recruiter', 'hiring_manager', 'viewer');

// Job permissions
export const canCreateJob = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canUpdateJob = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canDeleteJob = (user: User | null) => hasRole(user, 'admin');
export const canRollbackJob = (user: User | null) => hasRole(user, 'admin', 'recruiter');

// Search and Match permissions
export const canSearch = (user: User | null) => hasRole(user, 'admin', 'recruiter', 'hiring_manager', 'viewer');
export const canMatch = (user: User | null) => hasRole(user, 'admin', 'recruiter', 'hiring_manager');

// Analytics and Audit permissions
export const canViewAnalytics = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canViewAuditLog = (user: User | null) => hasRole(user, 'admin');

// Export permissions
export const canExport = (user: User | null) => hasRole(user, 'admin', 'recruiter');
export const canExportSensitive = (user: User | null) => hasRole(user, 'admin');

// User management permissions
export const canManageUsers = (user: User | null) => hasRole(user, 'admin');
